import { api, ApiError } from '../../../lib/api';

export type HtmlExportOptions = {
  include3D: boolean;
  viewer_mode?: 'none' | 'static' | 'interactive';
  viewer_link_expires_days?: number;
  /** Id du document IFC à partager (mode interactif). Si absent et plusieurs IFC, 400. */
  ifc_document_id?: string;
};

/**
 * Clone le DOM rendu (ref vers le container `.pres-theme-wrap`), inline les images
 * et vidéos, applique le mode 3D choisi, injecte un lien IFC signé expirant si
 * mode interactif, emballe dans un HTML autonome avec tout le CSS du document.
 *
 * Utilisé par Présentation ET Composition : même pipeline pour que l'export
 * HTML soit identique dans les deux onglets.
 *
 * @param exportEl — élément HTML cloné (doit être `.pres-theme-wrap`). Obtenu
 *   via `ref.current` dans le composant appelant.
 * @param title — titre affiché dans l'en-tête et nom de fichier.
 * @param projectId — utilisé pour demander un lien IFC signé en mode interactif.
 * @param opts — mode 3D + paramètres.
 */
export async function exportHtmlStandalone({
  exportEl,
  title,
  projectId,
  opts,
}: {
  exportEl: HTMLElement;
  title: string;
  projectId: number;
  opts: HtmlExportOptions;
}): Promise<void> {
  const viewerMode = opts.viewer_mode ?? 'static';
  const clone = exportEl.cloneNode(true) as HTMLElement;

  // Retire les UI d'édition (drag handles, toolbars, pickers) — pas de sens dans un fichier partagé.
  clone
    .querySelectorAll(
      '.slide-actions, .slide-width-picker, .slide-drag-handle, .chart-suggestions'
    )
    .forEach((n) => n.remove());

  if (viewerMode === 'none') {
    // Fichier ultra-léger : on retire toutes les images et vidéos.
    clone.querySelectorAll('img, video').forEach((n) => n.remove());
    opts = { ...opts, include3D: false };
  }

  // Inline les images (fetch → base64). Protège contre les 404 silencieusement.
  const imgs = Array.from(clone.querySelectorAll<HTMLImageElement>('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src') ?? '';
      if (!src || src.startsWith('data:')) return;
      try {
        const res = await fetch(src, { credentials: 'include' });
        if (!res.ok) return;
        const blob = await res.blob();
        const dataUrl = await blobToDataUrl(blob);
        img.setAttribute('src', dataUrl);
      } catch {
        /* image inaccessible — on laisse le src d'origine */
      }
    })
  );

  // Vidéos turntable / captures 3D.
  const vids = Array.from(clone.querySelectorAll<HTMLVideoElement>('video'));
  if (!opts.include3D) {
    for (const v of vids) {
      const placeholder = document.createElement('div');
      placeholder.style.cssText =
        "background: var(--c-nav, #F0EDE5); border: 1px dashed var(--c-border, #DDD8CE); padding: 24px; text-align: center; border-radius: 8px; color: var(--c-muted, #A9A193); font-size: 12px;";
      placeholder.textContent = 'Capture 3D (vidéo omise à l\'export léger)';
      v.replaceWith(placeholder);
    }
  } else {
    await Promise.all(
      vids.map(async (v) => {
        const src = v.getAttribute('src') ?? '';
        if (!src || src.startsWith('data:')) return;
        try {
          const res = await fetch(src, { credentials: 'include' });
          if (!res.ok) return;
          const blob = await res.blob();
          const dataUrl = await blobToDataUrl(blob);
          v.setAttribute('src', dataUrl);
          v.setAttribute('controls', '');
          v.setAttribute('loop', '');
          v.setAttribute('playsinline', '');
          v.removeAttribute('autoplay');
          v.removeAttribute('muted');
        } catch {
          /* noop */
        }
      })
    );
  }

  // Collecte toutes les règles CSS du document pour que l'export rende à l'identique.
  let css = '';
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = (sheet as CSSStyleSheet).cssRules;
      if (!rules) continue;
      for (const rule of Array.from(rules)) css += rule.cssText + '\n';
    } catch {
      /* cross-origin : skip */
    }
  }

  // Mode interactif : demande un lien IFC signé pour la bannière en haut du HTML.
  // Cas d'erreur possibles :
  //   - 404 : aucun IFC dans le projet → on prévient l'utilisateur via alert(),
  //   - 400 : plusieurs IFC sans ifc_document_id → on prévient pour qu'il choisisse,
  //   - autre : on log + omet la bannière silencieusement.
  let ifcBanner = '';
  let ifcError: string | null = null;
  if (viewerMode === 'interactive') {
    try {
      const r = await api.post<{ url?: string; expires_at?: string; filename?: string }>(
        `/projects/${projectId}/synthesis/ifc-share`,
        {
          expires_days: opts.viewer_link_expires_days ?? 30,
          ifc_document_id: opts.ifc_document_id,
        }
      );
      if (r?.url) {
        const dateTxt = r.expires_at
          ? new Date(r.expires_at).toLocaleDateString('fr-CH')
          : '—';
        const fileLabel = r.filename ? ` (${r.filename})` : '';
        // La bannière inclut un script qui avertit l'utilisateur si le HTML a été
        // ouvert depuis file://. Le lien fonctionne même dans ce cas (cible externe),
        // mais certains fetch conditionnels pourraient échouer → on clarifie l'UX.
        ifcBanner = `
<div class="ifc-share-banner" style="max-width:1200px;margin:0 auto 18px;padding:14px 18px;background:#F0EDE5;border:1px solid #DDD8CE;border-radius:10px;font-size:13px">
  <strong>Maquette 3D${escapeHtml(fileLabel)}</strong> — Visualisez-la dans un viewer 3D
  interactif en ligne, via un lien temporaire jusqu'au ${escapeHtml(dateTxt)} :
  <a href="${escapeHtml(r.url)}" target="_blank" rel="noopener" style="color:#3D5A45;text-decoration:underline">Ouvrir la maquette 3D dans un nouvel onglet</a>.
  <div style="margin-top:6px;font-size:11px;color:#6B7C72">Nécessite une connexion internet au moment de l'ouverture (le viewer charge le fichier IFC depuis le serveur).</div>
  <div id="__ifc-file-warning" style="display:none;margin-top:8px;padding:8px 10px;background:#FFF6E5;border:1px solid #E6C7A2;border-radius:6px;color:#8A5C1E;font-size:12px"></div>
</div>
<script>
  (function(){
    if (window.location.protocol === 'file:') {
      var w = document.getElementById('__ifc-file-warning');
      if (w) { w.style.display = 'block'; w.textContent = 'Vous avez ouvert ce fichier HTML en local (file://). Le lien ci-dessus fonctionne quand même — il vous ouvrira le viewer 3D dans un nouvel onglet depuis Internet. Pour partager, envoyez plutôt directement l\\'URL du viewer.'; }
    }
  })();
</script>`;
      }
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      const msg = e instanceof Error ? e.message : String(e);
      ifcError =
        status === 404
          ? 'Aucun fichier IFC dans ce projet — la bannière Maquette 3D ne sera pas incluse.'
          : status === 400
          ? 'Plusieurs modèles IFC disponibles : choisis lequel partager avant d\'exporter.'
          : `Lien IFC indisponible — la bannière ne sera pas incluse. (${msg})`;
      // eslint-disable-next-line no-console
      console.warn('[exportHtml] IFC share unavailable:', { status, msg });
    }
  }
  if (ifcError) {
    // On remonte l'info à l'utilisateur avant de continuer l'export (pas d'interruption :
    // l'export se fait quand même, juste sans bannière).
    try { window.alert(ifcError); } catch { /* noop */ }
  }

  const html = `<!doctype html>
<html lang="fr"><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${escapeHtml(title)}</title>
<style>${css}
body { margin: 0; padding: 24px; background: var(--c-bg, #FAFAF5); font-family: -apple-system, 'Segoe UI', sans-serif; color: var(--c-text, #3D5A45); }
.standalone-header { max-width: 1200px; margin: 0 auto 18px; display: flex; align-items: baseline; justify-content: space-between; }
.standalone-header h1 { font-size: 22px; margin: 0; }
.standalone-header .meta { font-size: 11px; color: var(--c-muted, #A9A193); }
#root-export { max-width: 1200px; margin: 0 auto; }
@media print { @page { size: A4 landscape; margin: 10mm; } body { padding: 0; background: #fff; } }
</style>
</head><body>
<div class="standalone-header">
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">Exporté le ${escapeHtml(new Date().toLocaleString('fr-CH'))} · Portail BIM</div>
</div>
${ifcBanner}
<div id="root-export">${clone.outerHTML}</div>
</body></html>`;

  // Diagnostic : poids approximatif du HTML généré (cible utile si vide).
  // eslint-disable-next-line no-console
  console.debug('[exportHtml] size', html.length, 'viewerMode', viewerMode, 'ifcBanner', !!ifcBanner);

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `synthese-${slugify(title)}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(blob);
  });
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!)
  );
}

function slugify(s: string): string {
  return String(s ?? 'export')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'export';
}
